import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../stores/auth.store';
import { useMundusStore } from '../stores/mundus.store';
import { MundusGridSkeleton } from '../components/Skeleton';
import api from '../lib/api';

/**
 * Simplified Dashboard — single workspace model.
 * If user has a Mundus, redirect to it. If not, create one automatically.
 */
export default function Dashboard() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { munduses, fetchMunduses, isLoading } = useMundusStore();
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    fetchMunduses();
  }, [fetchMunduses]);

  useEffect(() => {
    if (isLoading || creating) return;

    if (munduses.length > 0) {
      // Redirect to the first (and ideally only) Mundus
      navigate(`/mundus/${munduses[0].id}`, { replace: true });
    } else if (user && !creating) {
      // Auto-create a Mundus for the user
      autoCreateMundus();
    }
  }, [munduses, isLoading, user, creating]);

  const autoCreateMundus = async () => {
    setCreating(true);
    try {
      // Start demo which auto-creates Career Accelerator
      const res = await api.post('/gateway/demo/start', {
        jobTitle: 'Professional',
        goals: 'Achieve my goals',
        locale: user?.locale || 'en',
      });

      if (res.data.success) {
        navigate(`/mundus/${res.data.mundus.id}`, { replace: true });
      }
    } catch (error: any) {
      // Demo already active — fetch munduses again
      if (error.response?.data?.error === 'DemoAlreadyActive') {
        await fetchMunduses();
      } else {
        console.error('Failed to create Mundus:', error);
      }
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <div className="text-center">
        <MundusGridSkeleton />
        <p className="mt-4 text-slate-500 text-sm">
          {creating ? 'Setting up your workspace...' : 'Loading...'}
        </p>
      </div>
    </div>
  );
}
